// src/components/AvatarViewer.tsx
// VK-style avatar viewer modal with navigation, actions and chat creation.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@react/components';
import { cn } from '@core/ui';
import { CreateConversation } from '@core/api/endpoints/chat/create_conversation';
import type { Avatar } from '@core/types/userprofile';

interface AvatarViewerProps {
  avatars: Avatar[];
  initialIndex: number;
  onClose: () => void;
  onActivate?: (avatarId: string) => void;
  onDelete?: (avatarId: string) => void;
  isOwner?: boolean;
  ownerName?: string;
  t: (key: string) => string;
}

export function AvatarViewer({
  avatars,
  initialIndex,
  onClose,
  onActivate,
  onDelete,
  isOwner = false,
  ownerName = '',
  t,
}: AvatarViewerProps) {
  const [index, setIndex] = useState(initialIndex);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const navigate = useNavigate();

  const avatar = avatars[index];
  const imgUrl = avatar?.url || avatar?.thumbnail_url;
  const total = avatars.length;

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : total - 1));
  }, [total]);

  const goNext = useCallback(() => {
    setIndex((i) => (i < total - 1 ? i + 1 : 0));
  }, [total]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, goPrev, goNext]);

  const handleCreateChat = async () => {
    if (!avatar?.entity) return;
    setIsCreatingChat(true);
    try {
      const resp = await CreateConversation({
        type: 'CONVERSATION_TYPE_GROUP',
        name: `${ownerName ? ownerName + ' — ' : ''}${t('userprofile.avatarDiscussion')}`,
        description: avatar.original_filename || '',
        avatar_url: imgUrl,
        parent: avatar.entity,
      });
      if (resp?.conversation?.entity?.id) {
        onClose();
        navigate(`/chats/${resp.conversation.entity.id}`);
      }
    } catch {
      // silently fail
    } finally {
      setIsCreatingChat(false);
    }
  };

  const sizeKb = avatar?.size_bytes ? Math.round(avatar.size_bytes / 1024) : null;
  const uploadDate = avatar?.created_at
    ? new Date(avatar.created_at as string).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  if (!avatar) return null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 text-white/70 hover:text-white transition-colors p-2"
      >
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Main content area */}
      <div className="relative flex flex-1 items-center" onClick={(e) => e.stopPropagation()}>
        {/* Left arrow */}
        {total > 1 && (
          <button
            onClick={goPrev}
            className="absolute left-2 sm:left-6 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Image container */}
        <div className="flex-1 flex items-center justify-center px-16 sm:px-24">
          <img
            src={imgUrl}
            alt={avatar.original_filename}
            className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain select-none"
            draggable={false}
          />
        </div>

        {/* Right arrow */}
        {total > 1 && (
          <button
            onClick={goNext}
            className="absolute right-2 sm:right-6 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Right sidebar panel (VK-style) */}
      <div
        className="relative w-80 bg-card border-l border-border flex flex-col shrink-0 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Photo counter */}
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">
            {t('userprofile.photoOf').replace('{current}', String(index + 1)).replace('{total}', String(total))}
          </h3>
          {uploadDate && (
            <p className="text-xs text-muted-foreground mt-1">{uploadDate}</p>
          )}
        </div>

        {/* File info */}
        <div className="px-5 py-3 border-b border-border space-y-1.5">
          {avatar.original_filename && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="truncate">{avatar.original_filename}</span>
            </div>
          )}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {sizeKb && (
              <span>{sizeKb} KB</span>
            )}
            {avatar.content_type && (
              <span>{avatar.content_type.split('/')[1]?.toUpperCase()}</span>
            )}
          </div>
        </div>

        {/* Active badge */}
        {avatar.is_active && (
          <div className="px-5 py-2.5 border-b border-border">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('userprofile.currentAvatar')}
            </span>
          </div>
        )}

        {/* Actions */}
        {isOwner && (
          <div className="px-5 py-4 border-b border-border space-y-2">
            {!avatar.is_active && onActivate && (
              <Button
                variant="secondary"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={() => {
                  onActivate(avatar.entity?.id ?? '');
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {t('userprofile.setAsAvatar')}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="w-full justify-start gap-2"
              onClick={handleCreateChat}
              isLoading={isCreatingChat}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {t('userprofile.discussPhoto')}
            </Button>
            {onDelete && (
              <Button
                variant="danger"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={() => {
                  onDelete(avatar.entity?.id ?? '');
                  if (total <= 1) {
                    onClose();
                  } else {
                    setIndex((i) => (i >= total - 1 ? i - 1 : i));
                  }
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('common.delete')}
              </Button>
            )}
          </div>
        )}

        {/* Linked chat / aspects section */}
        {avatar.aspects?.has_aspects && (
          <div className="px-5 py-4 border-b border-border">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {t('userprofile.linkedChats')}
            </h4>
            <div className="space-y-1.5">
              {avatar.aspects.items.map((aspect) => (
                <button
                  key={aspect.entity?.id}
                  onClick={() => {
                    if (aspect.entity?.id) {
                      onClose();
                      navigate(`/chats/${aspect.entity.id}`);
                    }
                  }}
                  className="flex items-center gap-2 w-full text-left text-sm text-foreground hover:bg-muted px-2 py-1.5 rounded-md transition-colors"
                >
                  <svg className="w-4 h-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="truncate">{aspect.title || t('userprofile.discussion')}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Thumbnail strip at bottom */}
        {total > 1 && (
          <div className="mt-auto px-5 py-4 border-t border-border">
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {avatars.map((av, i) => (
                <button
                  key={av.entity?.id}
                  onClick={() => setIndex(i)}
                  className={cn(
                    'shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all',
                    i === index
                      ? 'border-primary ring-1 ring-primary/30'
                      : 'border-transparent opacity-60 hover:opacity-100',
                  )}
                >
                  <img
                    src={av.thumbnail_url || av.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
